import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { useGuestMode } from '../hooks/useGuestMode';
import { AppHeader } from '../components/AppHeader';
import { getActiveRoom } from '../components/RoomNotifier';
import { useSmartBack } from '../hooks/useSmartBack';
import { useAppModal } from '../components/Modal';
import './LobbyPage.css';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface RoomInfo {
  room_id: string;
  room_code: string;
  room_name?: string;
  host: string;
  host_user_id: string;
  player_count: number;
  max_players: number;
  difficulty: number;
  status: string;
  is_member?: boolean;
}


export function LobbyPage() {
  const navigate = useNavigate();
  const smartBack = useSmartBack('/');
  const { t } = useI18n();
  const { user, token } = useAuth();
  const { isGuest } = useGuestMode();
  const isGuestUser = !user && isGuest;
  const { showAlert, modalElement } = useAppModal();
  const [nickname, setNickname] = useState(() => localStorage.getItem('sudoku_nickname') || '');

  // Yeni tarayıcıda sudoku_nickname yoksa kullanıcının kayıt takma adını kullan
  useEffect(() => {
    if (!nickname && user?.nickname) {
      setNickname(user.nickname);
      localStorage.setItem('sudoku_nickname', user.nickname);
    }
  }, [user?.nickname]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [rooms, setRooms] = useState<RoomInfo[]>([]);
  const [friendUserIds, setFriendUserIds] = useState<Set<string>>(new Set());
  const [joinCode, setJoinCode] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createDifficulty, setCreateDifficulty] = useState(3);
  const [isPublic, setIsPublic] = useState(true);
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [roomName, setRoomName] = useState('');
  const [allowHints, setAllowHints] = useState(true);
  const [allowCandidates, setAllowCandidates] = useState(true);
  const activeRoom = getActiveRoom();
  const [playerId] = useState(() => {
    if (user?.id) return user.id;
    let id = localStorage.getItem('sudoku_player_id');
    if (!id) {
      id = 'p_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem('sudoku_player_id', id);
    }
    return id;
  });

  // Arkadaş listesini yükle (giriş yapılmışsa)
  useEffect(() => {
    if (!token) return;
    fetch(`${SOCKET_URL}/friends`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.friends) {
          setFriendUserIds(new Set(data.friends.map((f: { user_id: string }) => f.user_id)));
        }
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    const s = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: token ? { token } : {},
    });
    setSocket(s);

    s.on('connect', () => {
      setConnected(true);
      s.emit('get_rooms', { player_id: playerId });
    });

    s.on('disconnect', () => {
      setConnected(false);
    });

    s.on('rooms_list', (data: RoomInfo[]) => {
      setRooms(data);
    });

    s.on('room_created', (data: any) => {
      setCreating(false);
      navigate(`/room/${data.room_id}`, { state: { roomData: data, socket: true } });
    });

    s.on('room_joined', (data: any) => {
      setJoining(false);
      navigate(`/room/${data.room_id}`, { state: { roomData: data, socket: true } });
    });

    s.on('error', (data: any) => {
      setCreating(false);
      setJoining(false);
      showAlert(data.message);
    });

    // Periyodik oda yenileme
    const interval = setInterval(() => s.emit('get_rooms', { player_id: playerId }), 5000);

    return () => {
      clearInterval(interval);
      s.disconnect();
    };
  }, [navigate, token]);

  const saveNickname = () => {
    localStorage.setItem('sudoku_nickname', nickname);
  };

  const createRoom = () => {
    if (!nickname.trim()) { showAlert(t.lobby.nicknameRequired); return; }
    if (!connected) { showAlert(t.lobby.connecting); return; }
    saveNickname();
    setCreating(true);
    socket?.emit('create_room', {
      nickname: nickname.trim(),
      difficulty: createDifficulty,
      is_public: isPublic,
      max_players: maxPlayers,
      player_id: playerId,
      room_name: roomName.trim() || undefined,
      allow_hints: allowHints,
      allow_candidates: allowCandidates,
      user_id: user?.id || '',
    });
    // Timeout: 10 saniye içinde yanıt gelmezse iptal
    setTimeout(() => setCreating(false), 10000);
  };

  const joinByCode = () => {
    if (!nickname.trim()) { showAlert(t.lobby.nicknameRequired); return; }
    if (!joinCode.trim()) { showAlert(t.lobby.codeRequired); return; }
    if (!connected) { showAlert(t.lobby.connecting); return; }
    saveNickname();
    setJoining(true);
    socket?.emit('join_room', {
      room_code: joinCode.trim().toUpperCase(),
      nickname: nickname.trim(),
      player_id: playerId,
      user_id: user?.id || '',
    });
    setTimeout(() => setJoining(false), 10000);
  };

  const joinRoom = (roomId: string) => {
    if (!nickname.trim()) { showAlert(t.lobby.nicknameRequired); return; }
    if (!connected) { showAlert(t.lobby.connecting); return; }
    saveNickname();
    setJoining(true);
    socket?.emit('join_room', {
      room_id: roomId,
      nickname: nickname.trim(),
      player_id: playerId,
      user_id: user?.id || '',
    });
    setTimeout(() => setJoining(false), 10000);
  };


  return (
    <div className="lobby-page">
      <SEO path="/lobby" />
      <AppHeader onBack={smartBack} backLabel={t.common.backToHome} title={t.lobby.title} />
      {modalElement}
      {!connected && <p className="connecting-badge">{t.lobby.connectingBadge}</p>}

      {/* Misafir → üye ol daveti */}
      {isGuestUser && (
        <div className="guest-invite-banner">
          <span className="guest-invite-text">{t.home.guestSaveBannerText}</span>
          <button
            type="button"
            className="guest-invite-cta"
            onClick={() => navigate('/auth', { state: { from: '/lobby' } })}
          >
            {t.home.guestLockRegisterNow}
          </button>
        </div>
      )}

      {/* Nickname */}
      <div className="lobby-section">
        <label className="section-label">{t.lobby.nickname}</label>
        <input
          type="text"
          className="nickname-input"
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          placeholder={t.lobby.nicknamePlaceholder}
          maxLength={20}
        />
      </div>

      {/* Join by Code */}
      <div className="lobby-section">
        <label className="section-label">{t.lobby.joinByCode}</label>
        <div className="join-row">
          <input
            type="text"
            className="code-input"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            placeholder={t.lobby.codePlaceholder}
            maxLength={6}
          />
          <button className="join-btn" onClick={joinByCode} disabled={joining || !connected}>
            {joining ? '...' : t.lobby.joinBtn}
          </button>
        </div>
      </div>

      {/* Create Room */}
      <div className="lobby-section">
        <button className="create-toggle" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? t.lobby.closeCreate : t.lobby.createRoom}
        </button>
        {showCreate && (
          <div className="create-form">
            <div className="form-row">
              <label>{t.lobby.roomName}</label>
              <input
                type="text"
                value={roomName}
                onChange={e => setRoomName(e.target.value)}
                placeholder={t.lobby.roomNamePlaceholder}
                maxLength={30}
              />
            </div>
            <div className="form-row">
              <label>{t.lobby.difficultyLabel}</label>
              <select value={createDifficulty} onChange={e => setCreateDifficulty(Number(e.target.value))}>
                {[1,2,3,4,5].map(d => (
                  <option key={d} value={d}>{t.difficulties[d]}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>{t.lobby.roomType}</label>
              <select value={isPublic ? 'public' : 'private'} onChange={e => setIsPublic(e.target.value === 'public')}>
                <option value="public">{t.lobby.publicRoom}</option>
                <option value="private">{t.lobby.privateRoom}</option>
              </select>
            </div>
            <div className="form-row">
              <label>{t.lobby.maxPlayers}</label>
              <select value={maxPlayers} onChange={e => setMaxPlayers(Number(e.target.value))}>
                {[2,3,4,5,6,7,8].map(n => (
                  <option key={n} value={n}>{n} {t.lobby.playerCount}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>{t.lobby.allowHints}</label>
              <label className="form-toggle">
                <input type="checkbox" checked={allowHints} onChange={e => setAllowHints(e.target.checked)} />
                <span className="form-toggle-track" />
              </label>
            </div>
            <div className="form-row">
              <label>{t.lobby.allowCandidates}</label>
              <label className="form-toggle">
                <input type="checkbox" checked={allowCandidates} onChange={e => setAllowCandidates(e.target.checked)} />
                <span className="form-toggle-track" />
              </label>
            </div>
            <button className="create-btn" onClick={createRoom} disabled={creating || !connected}>
              {creating ? t.room.startingGame : t.lobby.createBtn}
            </button>
          </div>
        )}
      </div>

      {/* Public Rooms */}
      <div className="lobby-section">
        <label className="section-label">{t.lobby.openRooms}</label>
        {rooms.length === 0 ? (
          <div className="no-rooms">{t.lobby.noRooms}</div>
        ) : (
          <div className="rooms-list">
            {[...rooms].sort((a, b) =>
              (friendUserIds.has(b.host_user_id) ? 1 : 0) - (friendUserIds.has(a.host_user_id) ? 1 : 0)
            ).map(room => {
              const isFriendRoom = friendUserIds.has(room.host_user_id);
              const isMember = room.is_member ?? activeRoom?.room_id === room.room_id;
              const currentPlayers = Number(room.player_count) || 0;
              const roomMaxPlayers = Number(room.max_players) || 0;
              const isFull = roomMaxPlayers > 0 && currentPlayers >= roomMaxPlayers;
              const joinDisabled = joining || !connected || (isFull && !isMember);
              return (
                <div key={room.room_id} className={`room-card ${room.status === 'playing' ? 'room-card-playing' : ''} ${isFriendRoom ? 'room-card-friend' : ''}`}>
                  <div className="room-info">
                    <div className="room-name-row">
                      {room.room_name && <span className="room-name">{room.room_name}</span>}
                      {isFriendRoom && <span className="room-friend-badge">{t.profile.friendsBtn}</span>}
                    </div>
                    <span className="room-host">{room.host}</span>
                    <div className="room-meta-row">
                      <span className="room-difficulty">{t.difficulties[room.difficulty]}</span>
                      <span className="room-players">{currentPlayers}/{roomMaxPlayers} {t.lobby.playerCount}</span>
                      {room.status === 'playing' && (
                        <span className="room-status-playing">{t.lobby.statusPlaying}</span>
                      )}
                    </div>
                  </div>
                  <button className="join-room-btn" onClick={() => joinRoom(room.room_id)} disabled={joinDisabled}>
                    {joining ? '...' : isMember ? t.lobby.rejoinBtn : t.lobby.joinBtn}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
